/* eslint-disable no-alert */ import { connect } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Button } from 'reactstrap';
import Icon from 'jsx/components/core/icons/Icon';
import { cloneDeep } from 'lodash';
import FormInputSwitch from 'jsx/components/core/form/components/FormInputSwitch';
import { fetchAssumedAnimalClasses } from 'jsx/components/manage/actions/livestock_assumptions';

import FormInput from '../../../core/form/components/FormInput';
import FormBase from '../../../core/form/components/FormBase';
import {
  initControls,
  saveControls,
  updateControlOptions,
  updateControls,
  validateFormFieldControls,
} from '../../../core/form/lib/validateForm';
import { controls as animalClassControls } from '../forms/animalClasses';
import { fetchAttributes } from '../actions/attributes';
import {
  createAnimalClass,
  fetchAnimalClass,
  removeAnimalClass,
  updateAnimalClass,
  fetchAnimalClassUsages,
} from '../actions/animal_classes';

class AnimalClassModal extends FormBase {
  constructor(props) {
    super(props);

    this.state = {
      controls: cloneDeep(animalClassControls),
      data: {},
      id: null,
      isAdmin: false,
      isNew: false,
      isModalDeleteDisabled: false,
      title: 'Animal Class',
    };
  }

  componentDidMount() {
    this.props.dispatch(fetchAssumedAnimalClasses());
  }

  checkUserIsAdmin = () => {
    // Check if masquerading.
    if (this.props.auth.isMasquerade) return true;

    // Check current user is an admin.
    const { user_roles = [] } = this.props.profile.currentUser;
    if (user_roles.length === 0) return false;

    const userRoleTags = user_roles.map(({ role }) => role.tag);
    return userRoleTags.includes('admin');
  };

  async componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      const isAdmin = this.checkUserIsAdmin();
      const controls = initControls(cloneDeep(animalClassControls));
      let updatedState = {
        controls,
        data: {},
        id: null,
        isAdmin,
        isNew: true,
        title: 'New Animal Class',
      };

      // Populate the animal class types with the default division options.
      this.props.dispatch(
        fetchAttributes({ type: 'divisions', rules: { allowAnimalClasses: true } }),
      );

      if (this.props.id) {
        const { id } = this.props;
        updatedState = { ...updatedState, id, isNew: false, title: 'Edit Animal Class' };

        this.props.dispatch(fetchAnimalClassUsages(id)).then((isModalDeleteDisabled) =>
          this.setState({
            isModalDeleteDisabled,
          }),
        );

        this.props.dispatch(fetchAnimalClass(id)).then((data) => {
          const updatedData = cloneDeep(data);
          updatedData.division_id = updatedData.type.parent_id;
          if (updatedData.division_id) this.loadAttributes(updatedData.division_id);
          this.setState({
            data: updatedData,
            controls: updateControls(controls, updatedData),
          });
        });
      }

      this.setState(updatedState);
    }
  }

  onSave = async () => {
    let { data } = this.state;
    const { controls, isNew } = this.state;
    const { animal_class_types } = this.props.attributes;

    if (animal_class_types?.length === 0) controls.type_id.value = null;

    data = saveControls(controls, data);
    const { isValid, updatedControls } = await validateFormFieldControls(data, controls);

    if (isValid) {
      let success;
      if (isNew) {
        delete data.id;
        success = await this.props.dispatch(createAnimalClass(data));
      } else {
        success = await this.props.dispatch(updateAnimalClass(data));
      }

      if (success) this.onClose(true);
    } else {
      // Update controls state to display messages to the user
      this.setState({
        controls: updatedControls,
      });
    }
  };

  onClose = (refresh = false) => {
    if (refresh && this.props.onRefresh) this.props.onRefresh();
    this.props.setModal(false);
    this.props.dispatch({ type: 'UNSET_ANIMAL_CLASS_TYPES_ATTRIBUTES' });
  };

  onRemove = async () => {
    const { data } = this.state;

    const confirmed = window.confirm(`Removing ${data.name} class permanently. Continue?`);
    if (confirmed) {
      const success = await this.props.dispatch(removeAnimalClass(data.id));
      if (success) this.onClose(true);
    }
  };

  onChange = (event) => {
    const { controls } = this.state;
    const { value: old_division_id } = controls.division_id;
    this.handleChange(event);

    const { name, value } = event.target;

    switch (name) {
      case 'division_id':
        // Load another select;
        if (old_division_id !== value) {
          this.loadAttributes(value);
        }
        break;
      default:
        break;
    }
  };

  onCheckboxChange = (event) => {
    const { controls } = this.state;
    const { name, checked } = event.target;
    const updatedControls = cloneDeep(controls);
    updatedControls[name] = checked;
    this.setState({
      controls: updatedControls,
    });

    this.handleChange(event);
  };

  loadAttributes = (parent_id) => {
    if (parent_id === '' || parent_id === '-') {
      this.props.dispatch({ type: 'UNSET_ANIMAL_CLASS_TYPES_ATTRIBUTES' });
      return;
    }

    this.props.dispatch(fetchAttributes({ type: 'animal_class_types', parent_id }));
    this.props.dispatch(fetchAssumedAnimalClasses({ division_id: parent_id }));
  };

  render() {
    let { controls } = this.state;
    const { title, isAdmin, isNew, isModalDeleteDisabled } = this.state;
    const { isOpen } = this.props;
    const { responseMessage } = this.props.animal_classes;
    const { divisions, animal_class_types } = this.props.attributes;
    const { rows: livestockAssumptions } = this.props.livestock_assumptions.classes;

    const iconName = 'paw';

    let deleteDisabledTooltip;
    let nonEditableTooltip;

    // Check if benchmarking is enabled for livestock division
    let showBenchmarking = (controls.division_id.value);
    if (controls.division_id.value) {
      const division = divisions.find(div => div.id === controls.division_id.value);
      showBenchmarking = division?.rules.enableBenchmarking;
    }

    if (isModalDeleteDisabled)
      deleteDisabledTooltip = 'Transactions exist, cannot delete this class';

    if (!isNew) nonEditableTooltip = 'Cannot edit Division and Class Type for existing classes';

    controls.type_id.disabled = animal_class_types.length === 0;
    const optionsCriteria = [
      { key: 'assumed_type_id', data: livestockAssumptions },
      { key: 'division_id', data: divisions },
      { key: 'type_id', data: animal_class_types },
    ];

    optionsCriteria.forEach(({ key, data }) => {
      controls = updateControlOptions(controls, key, data);
    });

    return (
      <Modal isOpen={isOpen}>
        <ModalHeader className="bg-corporate text-white">
          <Icon size="1x" name={iconName} className="mr-2" />
          {title}
        </ModalHeader>
        <ModalBody>
          {responseMessage && <div className="text-center text-danger">{responseMessage}</div>}
          <Form>
            <FormInput handleChange={this.handleChange} control={controls.name} />
            <FormInput
              handleChange={this.onChange}
              control={controls.division_id}
              disabled={!isNew}
              title={nonEditableTooltip ?? null}
            />
            <FormInput
              handleChange={this.onChange}
              control={controls.type_id}
              disabled={!isNew}
              title={nonEditableTooltip ?? null}
            />
            {showBenchmarking && (
              <FormInput
                handleChange={this.onChange}
                control={controls.assumed_type_id}
                disabled={!isAdmin}
              />
            )}

            <FormInputSwitch
              control={controls.valid}
              className="ml-0 mr-0"
              handleChange={this.handleChange}
            />
          </Form>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          <div>
            <Button size="sm" className="mr-2" color="success" onClick={this.onSave}>
              Save
            </Button>
            <Button size="sm" color="light" onClick={this.onClose}>
              Cancel
            </Button>
          </div>
          {!isNew && (
            <Button
              size="sm"
              color="danger"
              onClick={this.onRemove}
              disabled={isModalDeleteDisabled}
              title={deleteDisabledTooltip ?? null}
            >
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStoreToProps = ({
  animal_classes,
  attributes,
  auth,
  livestock_assumptions,
  manage,
  profile,
}) => ({
  attributes,
  auth,
  animal_classes,
  livestock_assumptions,
  manage,
  profile,
});

export default connect(mapStoreToProps)(AnimalClassModal);
